// Territory-level business case — extracted from TerritoryPlanPage so it
// can be rendered INSIDE MapPage's right column when no zone is active.
//
// Props are all "dumb": the parent owns the state (plan, buildIndices,
// grossMargin, showModes) and passes setters. This component renders.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from '../lib/toast';
import GrantSelectorModal from './GrantSelectorModal';

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;
const fmtNum = (n: number) => Math.round(n).toLocaleString();
const fmtMo = (n: number | null) =>
  n == null || !isFinite(n) ? '—' : n < 12 ? `${n.toFixed(1)} mo` : `${(n / 12).toFixed(1)} yr`;

const MODE_COLOR: Record<string, string> = { fiber_build: '#f97316', wireless_ptp: '#0ea5e9', ride_along: '#10b981' };
const MODE_LABEL: Record<string, string> = { fiber_build: 'Fiber', wireless_ptp: 'Wireless', ride_along: 'Ride-along' };

export type ShowModes = { fiber_build: boolean; wireless_ptp: boolean; ride_along: boolean };

export default function TerritoryBusinessCase({
  plan, isAnalyzing, progress, grossMargin, setGrossMargin,
  buildIndices, setBuildIndices, showModes, setShowModes,
  onAnalyze, onDelete, onAbort, onGenerateReport, onZoneClick, onClearDrafts, clearDraftsPending,
  onRegenerateBackbone, regeneratingBackbone, hasHeadend,
}: {
  plan: any;
  isAnalyzing: boolean;
  progress: any;
  grossMargin: number;
  setGrossMargin: (v: number) => void;
  buildIndices: number[];
  setBuildIndices: (v: number[] | ((prev: number[]) => number[])) => void;
  showModes: ShowModes;
  setShowModes: (v: ShowModes | ((prev: ShowModes) => ShowModes)) => void;
  onAnalyze: (mode?: 'full' | 'bead_only' | 'bolt_on') => void;
  onDelete: () => void;
  onAbort?: () => void;
  onClearDrafts?: () => void;
  clearDraftsPending?: boolean;
  onGenerateReport: (extraQs?: string) => void;
  onZoneClick: (zoneId: string) => void;
  onRegenerateBackbone?: (overrideIndices?: number[]) => void;
  regeneratingBackbone?: boolean;
  hasHeadend?: boolean;
}) {
  const s = plan?.summary ?? {};
  // 2026-05-30: report-config modal state (Phase 2.5 quick version)
  const [reportConfigOpen, setReportConfigOpen] = useState(false);
  const [reportAudience, setReportAudience] = useState<string>('business');
  const [reportGrantDisplay, setReportGrantDisplay] = useState<string>('separate');
  const [reportDiscountRate, setReportDiscountRate] = useState<number>(8);
  // 2026-05-27: include 'committed' so the business case panel renders for
  // plans opened from Plans of Record. Committed plans are frozen snapshots
  // of complete plans; they have the same summary data, just locked.
  const isComplete = plan?.status === 'complete' || plan?.status === 'committed';
  // 2026-05-30: pull RDOF financial summary for this plan (Phase 2).
  // Endpoint aggregates per-cluster RDOF passings by state and computes
  // the 10-year support contribution. Only fires when the plan is
  // analyzed and has clusters.
  const rdofSummaryQ = useQuery<{
    total_rdof_passings: number;
    total_support_10yr_usd: number;
    total_monthly_support_usd: number;
    per_state: Array<{
      state: string; rdof_passings: number;
      award_loc_count: number; award_total_10yr_usd: number;
      dollars_per_loc_10yr: number;
      cluster_contribution_10yr_usd: number;
      cluster_contribution_monthly_usd: number;
      provider: string; sac: string;
    }>;
  }>({
    queryKey: ['plan-rdof-summary', plan?.id, plan?.status],
    queryFn: () => plan?.id ? api.get(`/territory/plans/${plan.id}/rdof-summary`) : Promise.reject(),
    enabled: !!plan?.id && (plan.status === 'complete' || plan.status === 'committed'),
    staleTime: 60_000,
  });
  // 2026-06-12 (task #7): funding-discovery — grants applicable to this territory.
  const fundingQ = useQuery<{
    state: string | null;
    passings: { total: number; bead: number; rdof: number; private: number };
    program_count: number; open_count: number;
    selected_keys: string[]; selected_count: number;
    programs: Array<{ program_key: string; name: string; agency: string; level: string; tier: string; selected?: boolean; funding_model?: string;
      status: string; min_speed: string; match_pct: number | null;
      max_award_usd?: number | null; federal_share_pct?: number | null; application_window?: string | null; eligibility_basis?: string | null; params_confidence?: string | null; params_reviewed?: boolean;
      is_enforceable_commitment: boolean; stacking_notes: string;
      sources: Array<{ title: string; url: string }>; }>;
  }>({
    queryKey: ['plan-funding-summary', plan?.id, plan?.status],
    queryFn: () => plan?.id ? api.get(`/territory/plans/${plan.id}/funding-summary`) : Promise.reject(),
    enabled: !!plan?.id,  // 2026-06-13: load grants pre-analyze too (Kevin steps 2-3)
    staleTime: 60_000,
  });
  const fundingQc = useQueryClient();
  const toggleGrant = async (key: string, currentlySelected: boolean) => {
    if (!plan?.id) return;
    const cur = fundingQ.data?.selected_keys || [];
    const next = currentlySelected ? cur.filter((k) => k !== key) : [...cur, key];
    try {
      await api.put(`/territory/plans/${plan.id}/funding-selection`, { program_keys: next });
      fundingQc.invalidateQueries({ queryKey: ['plan-funding-summary', plan.id, plan.status] });
    } catch (e) { /* ignore */ }
  };
  const [bridgeMsg, setBridgeMsg] = useState<string | null>(null);
  const [bridgeBusy, setBridgeBusy] = useState(false);
  const [grantModal, setGrantModal] = useState(false);
  const grantAutoOpened = useRef<string | null>(null);
  useEffect(() => {
    const apid = plan?.id ?? null;
    if (apid && grantAutoOpened.current !== apid && fundingQ.data && fundingQ.data.program_count > 0 && fundingQ.data.selected_count === 0) {
      grantAutoOpened.current = apid; setGrantModal(true);
    }
  }, [fundingQ.data, plan?.id]);
  // 2026-06-14: planning-stage aerial make-ready estimate (LiDAR canopy + slope)
  const mrEstQ = useQuery<{ progress: any; estimate: any }>({
    queryKey: ['mr-estimate', plan?.id],
    queryFn: () => api.get(`/territory/plans/${plan!.id}/makeready-estimate`),
    enabled: !!plan?.id,
    refetchInterval: (q) => { const s = (q.state.data as any)?.progress?.stage; return s && s !== 'done' ? 3000 : false; },
  });
  const mrEstRun = useMutation({
    mutationFn: () => api.post(`/territory/plans/${plan!.id}/makeready-estimate/run`, {}),
    onSuccess: () => fundingQc.invalidateQueries({ queryKey: ['mr-estimate', plan?.id] }),
  });
  const createComplianceProject = async () => {
    if (!plan?.id) return;
    setBridgeBusy(true); setBridgeMsg(null);
    try {
      const r: any = await api.post(`/territory/plans/${plan.id}/compliance-project`, {});
      setBridgeMsg(`${r.created ? 'Created' : 'Updated'} compliance project (${(r.active_grants || []).join(', ')}) - ${r.spawned} obligation(s) spawned. See Compliance > Grant Assignments / Permits.`);
    } catch (e: any) { setBridgeMsg(`Error: ${e?.message ?? e}`); }
    finally { setBridgeBusy(false); }
  };
  const buildSet = useMemo(() => new Set(buildIndices), [buildIndices]);
  // Independent BEAD subset selection — starts as "all checked" (same as private).
  // Stored as Set<number> of cluster indices whose BEAD subset is EXCLUDED.
  const [beadExcluded, setBeadExcluded] = useState<Set<number>>(new Set());
  // Sliders: aerial mix (100=all aerial cheap, 0=all buried BEAD-rate)
  // and take rate (% of passings that subscribe). Default to 100/40 to
  // match what auto-design assumed; user dials live to see effect.
  // Initial aerial-mix slider position. Auto-syncs to the design's actual
  // placement once pricing-model resolves (see effect below); this default
  // only matters for the first paint before that.
  const { data: economics } = useQuery<any>({
    queryKey: ['plant-economics'],
    queryFn: () => api.get('/settings/economics').catch(() => null),
    staleTime: 60 * 60 * 1000, retry: false,
  });
  const [aerialMixPct, setAerialMixPct] = useState<number>(() => Number(economics?.default_aerial_mix_pct ?? 100));
  const [takeRateUnservedPct, setTakeRateUnservedPct] = useState<number>(60);  // BEAD-eligible
  const [takeRateServedPct,   setTakeRateServedPct]   = useState<number>(25);  // bolt-on / private

  // Fetch the BEAD-priced pricing model at both slider extremes so we
  // can interpolate locally as the user drags the aerial/buried slider
  // (avoids per-pixel API calls). React Query caches forever within
  // staleTime, keyed on plan id + updated_at so re-analyze invalidates.
  const planUpdatedAt = plan?.updated_at ?? '';
  const planComplete = plan?.status === 'complete' || plan?.status === 'committed';
  const { data: pmAerial } = useQuery<any>({
    queryKey: ['pricing-model', plan?.id, planUpdatedAt, 'aerial'],
    queryFn: () => api.get(`/territory/plans/${plan.id}/pricing-model?aerialPct=100&takeRate=40`),
    enabled: !!plan?.id && planComplete && !!planUpdatedAt,
    staleTime: 10 * 60 * 1000,
  });
  const { data: pmBuried } = useQuery<any>({
    queryKey: ['pricing-model', plan?.id, planUpdatedAt, 'buried'],
    queryFn: () => api.get(`/territory/plans/${plan.id}/pricing-model?aerialPct=0&takeRate=40`),
    enabled: !!plan?.id && planComplete && !!planUpdatedAt,
    staleTime: 10 * 60 * 1000,
  });
  // Sync the aerial slider to the design's ACTUAL placement mix once
  // the pricing-model API responds. User can still override by dragging.
  const actualAerialPct = Number(pmAerial?.pricing_method?.sliders?.aerial_pct_actual);
  const didInitSlider = useRef(false);
  useEffect(() => {
    if (didInitSlider.current) return;
    if (Number.isFinite(actualAerialPct)) {
      setAerialMixPct(actualAerialPct);
      didInitSlider.current = true;
    }
  }, [actualAerialPct]);
  const beadSubsetChecked = (i: number) => !beadExcluded.has(i);

  // Fetch portfolio-wide average grant dollars per awarded BEAD BSL.
  const { data: grantRate } = useQuery<{ avg_grant_per_bsl: number; avg_federal_per_bsl: number; avg_match_per_bsl: number }>({
    queryKey: ['bead-grant-rate'],
    queryFn: () => api.get('/bead/grant-rate'),
    staleTime: 60 * 60 * 1000,
  });
  const avgGrantPerBSL = Number(grantRate?.avg_grant_per_bsl ?? 0);
  // Per-cluster subset selection:
  //   mainOn(i)  = private subset checked (main row)
  //   beadOn(i)  = BEAD subset checked (sub-row not in beadExcluded)
  // Cluster i contributes to totals PROPORTIONALLY by which subsets are checked.
  const mainOn = (i: number) => buildSet.has(i);
  const beadOn = (i: number) => !beadExcluded.has(i);

  const clusters = plan.clusters ?? [];
  const perCluster = s.per_cluster ?? [];
  let selCapex = 0;
  let selPassings = 0;
  let selMrrPriv = 0;
  let selMrrBead = 0;
  let selMrr = 0;  // legacy aggregate, kept for any downstream consumers
  for (let i = 0; i < perCluster.length; i++) {
    const c = perCluster[i];
    const beadCt = Number(clusters[i]?.bead_awarded_count ?? 0);
    const totalP = Number(c.passings ?? 0);
    const privCt = Math.max(0, totalP - beadCt);
    const rPriv = totalP > 0 ? privCt / totalP : 1;
    const rBead = totalP > 0 ? beadCt / totalP : 0;
    let   cCapex = Number(c.capex ?? 0);
    const cMrr   = Number(c.mrr ?? 0);
    // Take-rate slider scales drop-related capex linearly. Every mode
    // moves at least the activation portion (ONT install + drop install
    // labor only happen at customer activation). 'take_rate' mode also
    // moves the pre-build portion (drop terminal material), since drops
    // were modeled by take_rate at analyze time.
    const dm = (c as any).drop_modeling;
    if (dm && Number(dm.sellable_passings_for_drops) > 0) {
      const baselineTake = Number(dm.take_rate_used ?? 0.40);
      // For the drop-modeling adjustment, use a passings-weighted average
      // of the two segments. This captures the fact that BEAD vs private
      // demand both flow through drops; computing weight from the cluster's
      // BEAD ratio matches the loop above.
      const sliderTake = (takeRateUnservedPct / 100) * rBead + (takeRateServedPct / 100) * rPriv;
      const sp           = Number(dm.sellable_passings_for_drops);
      const baseDrops    = Math.round(sp * baselineTake);
      const newDrops     = Math.round(sp * sliderTake);
      const dropDelta    = newDrops - baseDrops;
      // Pick cost-per-drop based on what the analyze run modeled.
      // Fall back from one field to the other so data written by ANY
      // version of the analyze handler still moves the slider:
      //   - take_rate mode → prefer drop_unit_cost_all_in (full cost)
      //   - every_passing/persisted → prefer activation-only cost
      //   - missing mode (legacy data) → use whatever field is present
      const fullCost  = Number(dm.drop_unit_cost_all_in ?? 0);
      const actCost   = Number(dm.drop_activation_unit_cost_all_in ?? 0);
      const perDrop = dm.mode === 'take_rate'
        ? (fullCost || actCost)
        : (actCost  || fullCost);
      cCapex += dropDelta * perDrop;
    }
    if (mainOn(i)) {
      selPassings += privCt;
      selCapex += cCapex * rPriv;
      selMrrPriv += cMrr * rPriv;   // private-segment MRR (already-served / bolt-on)
    }
    if (beadOn(i) && beadCt > 0) {
      selPassings += beadCt;
      selCapex += cCapex * rBead;
      selMrrBead += cMrr * rBead;   // BEAD-segment MRR (unserved)
    }
  }
  // Use authoritative BEAD-in-territory count from summary, scaled by
  // the user's selection ratio. This matches the scenario row label
  // ("BEAD X/Y") and the per-project breakdown at the bottom of the BotB
  // box. The old per-cluster sum under-counted because main building
  // clusters don't carry bead_awarded_count — that field only exists
  // for orphan mini-clusters.
  const beadInTerritory = Number(plan.summary?.bead_in_territory ?? 0);
  const totalPlanPassings = Number(plan.summary?.total_passings ?? 0) || 1;
  const beadFraction = beadInTerritory > 0 ? beadInTerritory / totalPlanPassings : 0;
  const selBead    = Math.round(selPassings * beadFraction);
  const selPrivate = Math.max(0, selPassings - selBead);
  // Auto-design's quick estimate (catalog rates, all-aerial).
  const baseAdjCapex = selCapex + (s.total_backbone_capex ?? 0);
  const totalConstructionFt = Number((s as any).total_construction_ft ?? 0);
  const rateAerial = Number((s as any).labor_rate_aerial_per_ft ?? 3.20);
  const rateBuried = Number((s as any).labor_rate_buried_per_ft ?? 7.52);
  const blendedRate = (aerialMixPct / 100) * rateAerial + (1 - aerialMixPct / 100) * rateBuried;

  // Pull out the construction-labor line from each pricing-model run so
  // we can interpolate just that line; everything else is constant.
  const laborOf = (pm: any): number => {
    const labors = (pm?.line_items || []).filter((l: any) =>
      l.category === 'Labor' && /construction labor/i.test(l.sku)
    );
    return labors.reduce((acc: number, l: any) => acc + (Number(l.cost) || 0), 0);
  };
  const beadGrandAerial = Number(pmAerial?.totals?.grand_total ?? 0);
  const beadGrandBuried = Number(pmBuried?.totals?.grand_total ?? 0);
  // Lump-sum mode = totals don't vary with aerial slider (BEAD bid is fixed)
  const isLumpSumMode = pmAerial?.pricing_method?.dominant_project?.mode === 'lump_sum_allocation'
                        && Math.abs(beadGrandAerial - beadGrandBuried) < 1000;
  const dominantProj = pmAerial?.pricing_method?.dominant_project ?? null;
  const beadDirectAerial = Number(pmAerial?.totals?.direct ?? 0);
  const beadDirectBuried = Number(pmBuried?.totals?.direct ?? 0);
  const beadLaborAerial  = laborOf(pmAerial);
  const beadLaborBuried  = laborOf(pmBuried);
  const beadAddersAerial = Number(pmAerial?.totals?.adders ?? 0);
  const beadAddersBuried = Number(pmBuried?.totals?.adders ?? 0);
  // Materials/equipment etc are SAME across both runs (only labor varies).
  const beadMaterialsEtc = beadDirectAerial - beadLaborAerial;
  const beadAerialFrac = aerialMixPct / 100;

  // Interpolated BEAD-priced totals at the current slider position
  const adjLaborCost  = beadAerialFrac * beadLaborAerial  + (1 - beadAerialFrac) * beadLaborBuried;
  const adjAdders     = beadAerialFrac * beadAddersAerial + (1 - beadAerialFrac) * beadAddersBuried;
  // Cluster selection scaling: BEAD totals are computed for the whole
  // plan; scale by ratio of selected cluster capex to total to honor
  // user's checkbox selection in the rail.
  const totalClusterCapex = perCluster.reduce((acc: number, c: any) => acc + Number(c.capex || 0), 0);
  // FIX 2026-06-29: selectionRatio must be a true fraction of clusters selected (<= 1).
  // The old numerator `selCapex` is loop-accumulated with drop modeling + BEAD/private
  // splits, so it could exceed `totalClusterCapex` and inflate the BEAD-priced grand
  // total ~3.6x (the "$307M for a $86M build" bug). Use raw per-cluster capex on BOTH
  // sides so the ratio is a clean selected/total fraction.
  const selClusterCapexRaw = perCluster.reduce((acc: number, c: any, i: number) => acc + ((mainOn(i) || beadOn(i)) ? Number(c.capex || 0) : 0), 0);
  const selectionRatio = totalClusterCapex > 0 ? Math.min(1, selClusterCapexRaw / totalClusterCapex) : 1;
  const adjBeadDirect = (beadMaterialsEtc + adjLaborCost) * selectionRatio;
  // Add backbone capex on top — backbone connects clusters and is not in
  // the per-cluster BEAD price components above. Without this the headline
  // “Vistabeam at risk” silently drops the backbone cost the moment
  // BEAD pricing loads.
  const adjBeadGrand  = (beadMaterialsEtc + adjLaborCost + adjAdders) * selectionRatio + (s.total_backbone_capex ?? 0);
  const beadLoaded    = beadGrandAerial > 100_000 && beadGrandBuried > 100_000; // require BOTH and >$100k to trust BEAD price

  // adjCapex now == BEAD-priced grand total (slider-aware) when loaded;
  // falls back to auto-design's catalog estimate before the API resolves.
  const adjCapex = beadLoaded ? adjBeadGrand : baseAdjCapex;

  // Legacy laborDelta (catalog-based) only used for the slider's own
  // caption "+$X.XM vs baseline" so the delta tracks SOMETHING even
  // before the BEAD fetch finishes.
  const baseLaborInCapex = totalConstructionFt * rateAerial;
  const adjLaborCostCatalog = totalConstructionFt * blendedRate;
  const laborDelta = beadLoaded
    ? (adjLaborCost - beadLaborAerial)
    : (adjLaborCostCatalog - baseLaborInCapex);

  // Each segment scales from its own baseline (auto-design assumed 40% for
  // every passing; we now scale BEAD vs private separately to reflect very
  // different demand dynamics).
  const baseTakeRate = 0.40;
  selMrr = selMrrPriv + selMrrBead;  // back-compat aggregate
  const adjMrr =
    selMrrBead * ((takeRateUnservedPct / 100) / baseTakeRate) +
    selMrrPriv * ((takeRateServedPct   / 100) / baseTakeRate);
  const adjGm = adjMrr * grossMargin;
  const adjPayback = adjGm > 0 ? adjCapex / adjGm : null;

  // BotB-aware math (BEAD Colorado framework). Vistabeam's spend is
  // committed at the BotB total_project_cost; anything engineered
  // under BotB they still spend the full BotB, anything over they pay
  // the overage.
  // 2026-06-13 (step 6): BEAD is now a selectable funding choice. Backward-compatible:
  // empty selection => legacy (BEAD always applied, existing plans unchanged); any selection
  // => honor it (BEAD offset only if BEAD is in the selected set).
  const _fundSel = fundingQ.data?.selected_keys ?? null;
  const _beadActive = !_fundSel || _fundSel.length === 0 ? true : _fundSel.some((k) => /bead/i.test(k));
  const grantFederal = _beadActive ? Number(plan.summary?.grant_federal ?? 0) : 0;   // 75% reimb (prorated)
  const grantMatch   = _beadActive ? Number(plan.summary?.grant_match   ?? 0) : 0;   // 25% match (prorated)
  const botbCommitment = grantFederal + grantMatch;                 // total committed spend
  const maxGrantAvailable = grantFederal;                           // legacy alias for compat
  const grantBreakdown: any[] = plan.summary?.grant_breakdown_by_project ?? [];
  // 2026-06-01: unified math — both hero card and BotB card use the SAME
  // slider-aware engineered cost. Since no AR project has itemized BEAD bid
  // rates loaded, this is effectively Rate Card cost + slider-aware labor +
  // % adders. The variable is still named adjBeadGrand for back-compat, but
  // it's catalog-derived in practice for any project lacking rates.
  //
  // BotB model: ISP commits to spend at LEAST botbCommitment (fed + match).
  // If engineered > BotB → ISP pays match + overage.
  // If engineered < BotB → ISP still pays match (BEAD commitment, can't walk back).
  const totalBuildCapex = adjBeadGrand; // slider-aware engineered cost (Rate Card if no project rates)
  const botbVariance = totalBuildCapex - botbCommitment;
  const botbOverage = Math.max(0, botbVariance);
  const privateCapex = grantMatch + botbOverage; // BotB formula: match + overage
  const grantCoveredCapex = Math.min(totalBuildCapex, grantFederal);
  const grantSurplus = Math.max(0, grantFederal - totalBuildCapex);
  const adjCapexAfterGrant = privateCapex;

  if (!plan) {
    return (
      <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
        No territory yet. Draw a territory polygon on the map to get started.
        {onClearDrafts && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => { if (window.confirm('Delete any leftover draft zones that aren\'t part of a territory plan? Committed/analyzed zones are preserved.')) onClearDrafts(); }}
              disabled={!!clearDraftsPending}
              style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #64748b', background: '#fff', color: '#334155', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
            >
              {clearDraftsPending ? 'Clearing…' : 'Clear orphan draft zones'}
            </button>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
              If you drew polygons outside the Territory flow and they're stuck on the map, this clears them.
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13, padding: 12 }}>
      {grantModal && fundingQ.data && (
        <GrantSelectorModal fd={fundingQ.data} onToggle={toggleGrant} onClose={() => setGrantModal(false)} fmtNum={fmtNum} />
      )}
      {plan && !isComplete && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{plan.name}</div>
            {!isAnalyzing && (
              <button onClick={onDelete} style={{ fontSize: 10, color: '#dc2626', background: 'none', border: '1px solid #fca5a5', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Delete</button>
            )}
            {isAnalyzing && onAbort && (
              <button
                onClick={() => {
                  if (window.confirm(`Terminate analyze for "${plan.name}"? The plan will be marked failed. You can delete it or restart analysis afterward.`)) onAbort();
                }}
                style={{ fontSize: 10, color: '#fff', background: '#dc2626', border: '1px solid #b91c1c', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}
                title="Stop the running analyze. Useful when a plan has been stuck for many minutes."
              >■ Terminate</button>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            {(plan.clusters ?? []).length} zones · {fmtNum((plan.clusters ?? []).reduce((sum: number, c: any) => sum + c.passings, 0))} passings
          </div>
          {/* 2026-06-13: pre-analyze grant selection (Kevin's steps 2-3 — choose grants BEFORE analyze) */}
          {!isAnalyzing && fundingQ.data && fundingQ.data.program_count > 0 && (() => {
            const fd = fundingQ.data;
            return (
              <div style={{ marginBottom: 10, padding: 10, background: '#0f172a', color: '#e2e8f0', borderRadius: 6, border: '1px solid #334155' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc', textTransform:'uppercase', letterSpacing:'.05em' }}>Grants available - {fd.state ?? '-'}</span>
                  <span style={{ fontSize: 10, color: '#86efac' }}>{fd.selected_count} selected · {fd.programs.filter((p) => p.status !== 'closed' && p.status !== 'expired').length} active</span>
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8 }}>
                  Choose which grants to build against, then Analyze. Nothing selected = all private capital. <span style={{ color:'#34d399' }}>✓</span> = figures verified from official NOFA; others auto-discovered, confirm before relying.
                </div>
                <button onClick={() => setGrantModal(true)} style={{ width:'100%', padding:'7px 10px', fontSize:12, fontWeight:700, background:'#4338ca', color:'#fff', border:'none', borderRadius:5, cursor:'pointer' }}>
                  {fd.selected_count > 0 ? `Manage grants (${fd.selected_count} selected)` : `Select grants (${fd.programs.filter((p) => p.status !== 'closed' && p.status !== 'expired').length} available)`}
                </button>
                {fd.selected_count > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                    {fd.programs.filter((p) => p.selected).map((p) => (
                      <span key={p.program_key} style={{ fontSize:10, fontWeight:600, background:'#1e3a5f', color:'#cbd5e1', borderRadius:10, padding:'2px 8px' }}>{p.name}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          {!isAnalyzing && (() => {
            const [analyzeMode, setAnalyzeMode] = [
              (typeof window !== 'undefined' && (window as any).__terrAnalyzeMode) || 'full',
              (m: string) => { if (typeof window !== 'undefined') (window as any).__terrAnalyzeMode = m; },
            ];
            // Note: stored on window so the dropdown survives React re-renders.
            // Simple non-persistent UX — refresh resets to 'full'.
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
                  <span style={{ flex: '0 0 auto', fontWeight: 600 }}>Mode:</span>
                  <select
                    defaultValue={analyzeMode}
                    onChange={(e) => setAnalyzeMode(e.target.value)}
                    style={{
                      flex: 1, padding: '4px 8px', fontSize: 12,
                      border: '1px solid var(--border)', borderRadius: 4,
                      background: '#fff', cursor: 'pointer',
                    }}>
                    <option value="full">Full territory (every passing)</option>
                    <option value="bead_only">BEAD compliance only (unserved BSLs)</option>
                    <option value="bolt_on">Bolt-on (already-served addresses)</option>
                  </select>
                </div>
                <button
                  className="btn btn--primary"
                  onClick={() => onAnalyze((typeof window !== 'undefined' && (window as any).__terrAnalyzeMode) || 'full')}
                  style={{ width: '100%' }}>
                  ▶ Analyze all zones + backbone
                </button>
                <div style={{ fontSize: 10, color: 'var(--text-subtle, #94a3b8)', textAlign: 'center' }}>
                  Switching mode runs a fresh analyze. Existing plan results are overwritten.
                </div>
              </div>
            );
          })()}

          {isAnalyzing && (() => {
            const phase = progress.phase ?? 'analyzing_clusters';
            const done = progress.done ?? 0;
            const total = progress.total ?? 0;
            const isIndeterminate = phase === 'building_graph';
            const label =
              phase === 'building_graph' ? 'Building road graph… (one-time, ~15s)' :
              phase === 'computing_backbone' ? 'Computing backbone network…' :
              phase === 'complete' ? 'Done' :
              total > 0 ? `Analyzing zones (${done} of ${total} done)` : 'Analyzing…';
            const pct = total > 0 ? (done / total) * 100 : 0;
            return (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
                <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', marginTop: 6, position: 'relative' }}>
                  {isIndeterminate ? (
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, #8b5cf6 0%, #c4b5fd 50%, #8b5cf6 100%)', backgroundSize: '200% 100%', animation: 'ff-indet 1.2s linear infinite' }} />
                  ) : (
                    <div style={{ height: '100%', background: '#8b5cf6', width: `${Math.max(2, Math.min(98, pct))}%`, transition: 'width 0.5s', borderRadius: 4 }} />
                  )}
                </div>
                {phase !== 'building_graph' && total > 0 && done > 0 && done < total && (
                  <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>~{Math.max(1, Math.round((total - done) * 0.4))} min remaining</div>
                )}
                {progress.current && phase === 'analyzing_clusters' && (
                  <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 2 }}>Last completed: {progress.current}</div>
                )}
                <style>{`@keyframes ff-indet { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }`}</style>
              </div>
            );
          })()}
        </div>
      )}

      {/* Compare with another mode — sibling block, runs same polygon
          under a different demand filter so the user can compare cost
          numbers without re-drawing the polygon. */}
      {plan && (plan.status === 'complete' || plan.status === 'committed') && (() => {
        const cur = (plan?.analysis_mode ?? 'full') as string;
        const labelByMode: Record<string,string> = {
          full: 'Full territory',
          bead_only: 'BEAD compliance only',
          bolt_on: 'Bolt-on (already-served)',
        };
        const others = (['full','bead_only','bolt_on'] as const).filter(m => m !== cur);
        return (
          <div style={{
            padding: 10, border: '1px solid var(--border)',
            borderRadius: 6, background: 'var(--surface-2, #f8fafc)'
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 6 }}>
              Compare with another mode
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              Currently: <b>{labelByMode[cur] ?? cur}</b>. Spawn a sibling plan
              with the same polygon under a different mode (no re-drawing needed).
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {others.map((m) => (
                <button
                  key={m}
                  className="btn"
                  style={{ flex: '1 1 auto', fontSize: 11, padding: '6px 10px' }}
                  onClick={async () => {
                    try {
                      const r = await fetch(`/api/territory/plans/${plan.id}/spawn-mode`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ mode: m }),
                      });
                      if (!r.ok) {
                        const txt = await r.text();
                        alert(`Spawn failed: ${txt}`);
                        return;
                      }
                      const data = await r.json();
                      await fetch(`/api/territory/plans/${data.id}/analyze`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ mode: m }),
                      });
                      if (typeof window !== 'undefined') window.location.reload();
                    } catch (e: any) {
                      alert(`Spawn error: ${e?.message ?? String(e)}`);
                    }
                  }}
                >
                  ▶ Spawn {labelByMode[m] ?? m}
                </button>
              ))}
            </div>
          </div>
        );
      })()}
            {isComplete && s.per_cluster && (
        <>
          <button onClick={onDelete} style={{ fontSize: 10, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', alignSelf: 'flex-end' }}>Delete territory</button>
          <div style={{ padding: 14, borderRadius: 8, background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.08em', opacity: 0.8, textAlign: 'center', marginBottom: 6 }}>Territory · build cost</div>
            <div style={{ fontSize: 28, fontWeight: 800, textAlign: 'center', marginBottom: 8 }}>{fmtUSD(adjCapex)}</div>
            <div style={{ fontSize: 11, lineHeight: 1.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.85 }}>
                <span>− Federal grant (BEAD)</span>
                <span>{fmtUSD(Number(plan.summary?.grant_federal ?? 0))}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.25)', paddingTop: 4, marginTop: 4, fontWeight: 700 }}>
                <span>= ISP at risk</span>
                <span>{fmtUSD(privateCapex)}</span>
              </div>
            </div>
            <div style={{ fontSize: 10, opacity: 0.7, marginTop: 8, textAlign: 'center' }}>
              {Math.min(buildSet.size, s.per_cluster.length)} of {s.per_cluster.length} zones · {fmtNum(selPassings)} passings · {fmtMo(adjPayback)} payback
            </div>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)' }}>Gross margin</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#6366f1' }}>{(grossMargin * 100).toFixed(0)}%</span>
            </div>
            <input type="range" min={50} max={90} step={1} value={Math.round(grossMargin * 100)}
              onChange={(e) => setGrossMargin(Number(e.target.value) / 100)}
              style={{ width: '100%', accentColor: '#6366f1' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-subtle, #94a3b8)', marginTop: 2 }}>
              <span>50%</span><span>70%</span><span>90%</span>
            </div>
          </div>
          {/* Aerial vs Buried slider — hidden in lump-sum mode (bid is fixed) */}
          {isLumpSumMode ? (
            <div style={{ padding: '10px 12px', background: '#0f172a', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: '#94a3b8' }}>
              <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 4, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>BEAD bid in use</div>
              <div>Pricing locked to {dominantProj?.project_code} lump-sum bid (${(Number(dominantProj?.bid_total_prorated)/1e6).toFixed(2)}M committed at {dominantProj?.overlap_ratio_pct}% overlap). Aerial/buried mix doesn't affect committed cost.</div>
            </div>
          ) : (
          <div style={{ padding: '10px 12px', background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)' }}>
                Aerial / Buried mix
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#0ea5e9' }}>
                {aerialMixPct}% aerial · {100 - aerialMixPct}% buried
                {Number.isFinite(actualAerialPct) && aerialMixPct !== actualAerialPct && (
                  <a href="#" onClick={(e) => { e.preventDefault(); setAerialMixPct(actualAerialPct); }}
                     style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer' }}>
                    ↻ reset
                  </a>
                )}
              </span>
            </div>
            <input type="range" min={0} max={100} step={5} value={aerialMixPct}
              onChange={(e) => setAerialMixPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#0ea5e9' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-subtle, #94a3b8)', marginTop: 2 }}>
              <span>All buried (${rateBuried.toFixed(2)}/ft · BEAD UG)</span>
              <span>All aerial (${rateAerial.toFixed(2)}/ft · catalog)</span>
            </div>
            {totalConstructionFt > 0 && (
              <div style={{ fontSize: 10, color: 'var(--text-subtle, #94a3b8)', marginTop: 4 }}>
                {fmtNum(totalConstructionFt)} ft · blended ${blendedRate.toFixed(2)}/ft · labor {laborDelta >= 0 ? '+' : '−'}{fmtUSD(Math.abs(laborDelta))} vs baseline
              </div>
            )}
          </div>)}

          {/* Soil profile card — SSURGO-driven construction-method realism */}
          <SoilProfileCard planId={plan.id} aerialMixPct={aerialMixPct} aerialRate={rateAerial} />

          {/* 2026-06-14: aerial make-ready risk (LiDAR canopy + slope), scales with the slider */}
          {totalConstructionFt > 0 && (() => {
            const est = mrEstQ.data?.estimate;
            const stg = mrEstQ.data?.progress?.stage;
            const estRunning = mrEstRun.isPending || (!!stg && stg !== 'done' && !est);
            const aerialFt = totalConstructionFt * (aerialMixPct / 100);
            const aerialMi = aerialFt / 5280;
            const perFt = est ? Number(est.per_aerial_ft) : 0;
            const total = perFt * aerialFt;
            const perMi = perFt * 5280;
            return (
              <div style={{ padding: '10px 12px', background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)' }}>🌲 Tree-trim / vegetation (est)</span>
                  {est && <span style={{ fontSize: 16, fontWeight: 700, color: '#b45309' }}>{fmtUSD(total)}</span>}
                </div>
                {!est && !estRunning && (
                  <button onClick={() => mrEstRun.mutate()} style={{ width: '100%', padding: '6px', fontSize: 11, fontWeight: 700, background: '#4338ca', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                    Estimate from LiDAR (canopy + slope)
                  </button>
                )}
                {estRunning && <div style={{ fontSize: 11, color: '#1e3a8a' }}>⏳ Sampling canopy + terrain over the polygon…</div>}
                {est && (
                  <div style={{ fontSize: 10, color: 'var(--text-subtle, #94a3b8)', lineHeight: 1.5 }}>
                    {aerialMi.toFixed(1)} aerial-mi × ~{fmtUSD(perMi)}/mi · {Math.round(Number(est.pct_tall) * 100)}% tall canopy, {Math.round(Number(est.pct_steep) * 100)}% steep ground
                    <br />Engineered capex + contingency = <b style={{ color: 'var(--text)' }}>{fmtUSD(totalBuildCapex + total)}</b>
                    <br />Held as a separate tree-trim / vegetation contingency — not in engineered capex or grant / BotB math. Scales with the slider. Pole make-ready (attachments) is estimated separately on the Make-Ready Risk page once the route is pole-scanned. <a href="/compliance/makeready" style={{ color: '#6366f1' }}>Full per-segment scoring →</a>
                  </div>
                )}
              </div>
            );
          })()}

                    {/* Take-rate sliders — segmented (BEAD-unserved vs already-served) */}
          <div style={{ padding: '10px 12px', background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 8 }}>
              Take rate (segmented)
            </div>

            {/* BEAD / unserved segment */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>BEAD · unserved</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{takeRateUnservedPct}%
                {takeRateUnservedPct !== 60 && (
                  <a href="#" onClick={(e) => { e.preventDefault(); setTakeRateUnservedPct(60); }}
                     style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer', fontWeight: 400 }}>↻ 60%</a>
                )}
              </span>
            </div>
            <input type="range" min={10} max={90} step={1} value={takeRateUnservedPct}
              onChange={(e) => setTakeRateUnservedPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#10b981' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-subtle, #94a3b8)', marginTop: 1, marginBottom: 8 }}>
              <span>10%</span><span>60% baseline</span><span>90%</span>
            </div>

            {/* Already-served segment */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Already-served · bolt-on</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6' }}>{takeRateServedPct}%
                {takeRateServedPct !== 25 && (
                  <a href="#" onClick={(e) => { e.preventDefault(); setTakeRateServedPct(25); }}
                     style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer', fontWeight: 400 }}>↻ 25%</a>
                )}
              </span>
            </div>
            <input type="range" min={5} max={70} step={1} value={takeRateServedPct}
              onChange={(e) => setTakeRateServedPct(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#3b82f6' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--text-subtle, #94a3b8)', marginTop: 1 }}>
              <span>5%</span><span>25% baseline</span><span>70%</span>
            </div>

            <div style={{ fontSize: 10, color: 'var(--text-subtle, #94a3b8)', marginTop: 8, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
              Combined MRR adjusts to <strong style={{ color: 'var(--text)' }}>{fmtUSD(adjMrr)}/mo</strong>
            </div>
          </div>

          <button
            onClick={() => setReportConfigOpen(true)}
            style={{ width: '100%', padding: '10px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            ▸ Generate Business Case Report
          </button>
          {/* 2026-05-30 quick report-config popup (Phase 2.5).
              Full version with presets, preview pane, more knobs is in
              C:\tmp\ff_pm_build\TODO_full_report_config.md */}
          {reportConfigOpen && (
            <div
              role="dialog"
              aria-modal="true"
              onClick={(e) => { if (e.target === e.currentTarget) setReportConfigOpen(false); }}
              style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
            >
              <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 440, maxWidth: '92vw', boxShadow: '0 12px 36px rgba(0,0,0,0.35)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                  <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>Configure business case report</h3>
                  <button onClick={() => setReportConfigOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#64748b', cursor: 'pointer' }}>×</button>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Audience</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {([
                      ['business', 'Business case (default)'],
                      ['investor', 'Investor brief'],
                      ['sbo',      'State broadband office'],
                      ['exec',     'Internal executive'],
                    ] as Array<[string, string]>).map(([v, label]) => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', border: `1px solid ${reportAudience === v ? '#0f172a' : '#cbd5e1'}`, borderRadius: 4, background: reportAudience === v ? '#f1f5f9' : '#fff', fontSize: 12, cursor: 'pointer' }}>
                        <input type="radio" name="audience" value={v} checked={reportAudience === v} onChange={() => setReportAudience(v)} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Grant display</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {([
                      ['separate', 'Separately (BEAD upfront vs RDOF 10-yr stream)'],
                      ['combined-nominal', 'Combined nominal (BEAD + RDOF total $, no discount)'],
                      ['combined-npv', 'Combined NPV (BEAD + present-value RDOF stream)'],
                    ] as Array<[string, string]>).map(([v, label]) => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', border: `1px solid ${reportGrantDisplay === v ? '#0f172a' : '#cbd5e1'}`, borderRadius: 4, background: reportGrantDisplay === v ? '#f1f5f9' : '#fff', fontSize: 12, cursor: 'pointer' }}>
                        <input type="radio" name="grantDisplay" value={v} checked={reportGrantDisplay === v} onChange={() => setReportGrantDisplay(v)} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {reportGrantDisplay === 'combined-npv' && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                      <span>NPV discount rate</span>
                      <span style={{ fontSize: 13, color: '#0f172a' }}>{reportDiscountRate}%</span>
                    </div>
                    <input type="range" min={5} max={12} step={0.5} value={reportDiscountRate} onChange={(e) => setReportDiscountRate(Number(e.target.value))} style={{ width: '100%', accentColor: '#0f172a' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginTop: 2 }}><span>5% (low risk)</span><span>12% (high risk)</span></div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                  <button onClick={() => setReportConfigOpen(false)} style={{ padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button
                    onClick={() => {
                      const npvPart = reportGrantDisplay === 'combined-npv' ? `&discountRate=${reportDiscountRate}` : '';
                      const qs = `&aerialMix=${aerialMixPct}&takeRateUnserved=${takeRateUnservedPct}&takeRateServed=${takeRateServedPct}&audience=${reportAudience}&grantDisplay=${reportGrantDisplay}${npvPart}`;
                      onGenerateReport(qs);
                      setReportConfigOpen(false);
                    }}
                    style={{ padding: '8px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >Generate report ▸</button>
                </div>
              </div>
            </div>
          )}
          {/* KMZ export — opens directly via the browser, no JS fetch
              needed; cookies travel along so backend auth works. The
              download attribute hints the browser to save instead of
              navigate. Filename is set server-side via Content-Disposition. */}
          <a
            href={`/api/territory/plans/${plan.id}/kmz`}
            download
            style={{
              width: '100%', padding: '10px 14px', background: '#0f172a',
              color: '#fff', border: 'none',
              borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              textAlign: 'center', textDecoration: 'none', display: 'block',
            }}
            title="Export territory + clusters + fiber + POPs as a Google Earth-compatible .kmz"
          >
            ⬇ Export to Google Earth (.kmz)
          </a>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            <KPI label="Engineered capex" value={fmtUSD(adjCapex)} />
            <KPI label="Backbone" value={fmtUSD(s.total_backbone_capex)} />
            <KPI label="$/passing" value={selPassings > 0 ? fmtUSD(adjCapex / selPassings) : '—'} />
            <KPI label="MRR" value={`${fmtUSD(adjMrr)}/mo`} />
            <KPI label="Backbone mi" value={`${s.backbone_length_mi} mi`} />
            <KPI label="Payback" value={fmtMo(adjPayback)} color={adjPayback != null && adjPayback < 36 ? '#10b981' : '#f59e0b'} />
          </div>
          {/* BEAD vs private-capital split */}
          {selBead > 0 && botbCommitment > 0 && (
            <div style={{ padding: 10, background: '#064e3b', color: '#fff', borderRadius: 6, fontSize: 11 }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#6ee7b7', marginBottom: 6 }}>
                BEAD Commitment (BotB) vs Engineered Capex
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <div>BEAD-awarded passings</div><div style={{ textAlign: 'right', fontWeight: 700 }}>{fmtNum(selBead)}</div>
                <div>Private-capital passings</div><div style={{ textAlign: 'right', fontWeight: 700, color: '#fbbf24' }}>{fmtNum(selPrivate)}</div>

                <div style={{ fontWeight: 700, marginTop: 4 }}>BotB Commitment (prorated)</div>
                <div style={{ textAlign: 'right', fontWeight: 700, marginTop: 4 }}>{fmtUSD(botbCommitment)}</div>
                <div style={{ paddingLeft: 8, fontSize: 10, color: '#6ee7b7' }}>  Federal Grant (75%)</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#6ee7b7' }}>{fmtUSD(grantFederal)}</div>
                <div style={{ paddingLeft: 8, fontSize: 10, color: '#6ee7b7' }}>  Match, ISP (25%)</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#6ee7b7' }}>{fmtUSD(grantMatch)}</div>

                <div style={{ fontWeight: 700, marginTop: 4 }}>Engineered capex (slider-aware)</div>
                <div style={{ textAlign: 'right', fontWeight: 700, marginTop: 4 }}>{fmtUSD(totalBuildCapex)}</div>
                <div style={{ paddingLeft: 8, fontSize: 10, color: '#6ee7b7' }}>  Materials & equipment</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#6ee7b7' }}>{fmtUSD(beadMaterialsEtc * selectionRatio)}</div>
                <div style={{ paddingLeft: 8, fontSize: 10, color: '#6ee7b7' }}>  Construction labor (slider-aware)</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#6ee7b7' }}>{fmtUSD(adjLaborCost * selectionRatio)}</div>
                <div style={{ paddingLeft: 8, fontSize: 10, color: '#6ee7b7' }}>  Adders (contingency)</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#6ee7b7' }}>{fmtUSD(adjAdders * selectionRatio)}</div>

                <div>Variance vs BotB</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: botbVariance >= 0 ? '#fbbf24' : '#6ee7b7' }}>
                  {botbVariance >= 0 ? '+' : '−'}{fmtUSD(Math.abs(botbVariance))} {botbVariance >= 0 ? 'over' : 'under'}
                </div>

                <div style={{ fontWeight: 700, marginTop: 4 }}>Match and Private</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: '#fbbf24', marginTop: 4 }}>{fmtUSD(privateCapex)}</div>
                <div style={{ paddingLeft: 8, fontSize: 10, color: '#fbbf24' }}>  Committed match (25%)</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#fbbf24' }}>{fmtUSD(grantMatch)}</div>
                {botbOverage > 0 && (
                  <><div style={{ paddingLeft: 8, fontSize: 10, color: '#fbbf24' }}>  Overage beyond BotB</div>
                  <div style={{ textAlign: 'right', fontSize: 10, color: '#fbbf24' }}>{fmtUSD(botbOverage)}</div></>
                )}

                <div style={{ gridColumn: '1 / 3', fontSize: 10, color: '#6ee7b7', marginTop: 4 }}>
                  {botbVariance < 0
                    ? `Engineered ${fmtUSD(-botbVariance)} under BotB. ISP still commits the full $${(botbCommitment/1e6).toFixed(2)}M per BEAD agreement (25% match = ${fmtUSD(grantMatch)}).`
                    : botbVariance === 0
                    ? 'Engineered capex matches BotB commitment exactly. ISP pays 25% match only.'
                    : `Engineered ${fmtUSD(botbOverage)} OVER BotB. ISP funds overage out of pocket on top of 25% match.`}
                </div>
              </div>
              {grantBreakdown.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 10 }}>
                  <div style={{ color: '#6ee7b7', marginBottom: 3 }}>Per project in territory:</div>
                  {grantBreakdown.map((b: any) => (
                    <div key={b.project_code} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                      <span>{b.project_code} ({b.bsls_in_territory}/{b.awarded_bsl_count} BSLs = {b.ratio}%)</span>
                      <span>{fmtUSD(b.grant_for_this)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(110, 231, 183, 0.25)', textAlign: 'center' }}>
                <a
                  href={`/pricing-model?planId=${plan.id}&aerialPct=${aerialMixPct}&takeRateUnserved=${takeRateUnservedPct}&takeRateServed=${takeRateServedPct}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#6ee7b7', fontSize: 11, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  View pricing model (materials &amp; unit prices) →
                </a>
              </div>
            </div>
          )}

          {/* 2026-05-30 RDOF section (Phase 2). Renders when the plan touches
              any state with active RDOF awards. Per-state breakdown shows
              passings, this territory's 10-year support contribution + monthly
              stream, and the source award context. */}
          {rdofSummaryQ.data && rdofSummaryQ.data.total_rdof_passings > 0 && (
            <div style={{ padding: 10, background: '#155e75', color: '#fff', borderRadius: 6, fontSize: 11 }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#67e8f9', marginBottom: 6 }}>
                RDOF — 10-year support contribution
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <div>RDOF passings in territory</div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>{fmtNum(rdofSummaryQ.data.total_rdof_passings)}</div>

                <div style={{ fontWeight: 700, marginTop: 4 }}>10-yr support attributable</div>
                <div style={{ textAlign: 'right', fontWeight: 700, marginTop: 4 }}>{fmtUSD(rdofSummaryQ.data.total_support_10yr_usd)}</div>

                <div style={{ paddingLeft: 8, fontSize: 10, color: '#67e8f9' }}>  Monthly stream</div>
                <div style={{ textAlign: 'right', fontSize: 10, color: '#67e8f9' }}>{fmtUSD(rdofSummaryQ.data.total_monthly_support_usd)}/mo</div>

                {rdofSummaryQ.data.per_state.map((s) => (
                  <React.Fragment key={s.state}>
                    <div style={{ gridColumn: '1 / 3', borderTop: '1px solid #0e7490', marginTop: 6, paddingTop: 4, fontSize: 10, color: '#67e8f9', fontWeight: 600 }}>
                      {s.state} · {s.provider} (SAC {s.sac})
                    </div>
                    <div style={{ paddingLeft: 8 }}>  passings (territory / award)</div>
                    <div style={{ textAlign: 'right' }}>{fmtNum(s.rdof_passings)} / {fmtNum(s.award_loc_count)}</div>
                    <div style={{ paddingLeft: 8 }}>  contribution (10 yr)</div>
                    <div style={{ textAlign: 'right' }}>{fmtUSD(s.cluster_contribution_10yr_usd)}</div>
                    <div style={{ paddingLeft: 8, fontSize: 10, color: '#a5f3fc' }}>  @ ${s.dollars_per_loc_10yr.toFixed(2)}/loc/10yr</div>
                    <div style={{ textAlign: 'right', fontSize: 10, color: '#a5f3fc' }}>${(s.cluster_contribution_monthly_usd).toFixed(0)}/mo</div>
                  </React.Fragment>
                ))}

                <div style={{ gridColumn: '1 / 3', fontSize: 10, color: '#a5f3fc', marginTop: 6 }}>
                  $/loc rate = state award total ÷ original CAM count. RDOF support is paid as a monthly stream over 10 years from authorization. Contribution attribution is a naive prorate; refine when actual served vs authorized counts are tracked per location.
                </div>
              </div>
            </div>
          )}
          {/* 2026-06-12 (task #7): Funding-discovery panel from the shared GeoHub catalog. */}
          {isComplete && fundingQ.data && fundingQ.data.program_count > 0 && (() => {
            const fd = fundingQ.data;
            const badge = (s: string) => {
              const m: Record<string,[string,string]> = { open:['#065f46','#6ee7b7'], anticipated:['#1e40af','#93c5fd'], in_execution:['#854d0e','#fde68a'], closed:['#374151','#9ca3af'], unknown:['#374151','#9ca3af'] };
              const pair = m[s] || m.unknown;
              return <span style={{ background:pair[0], color:pair[1], fontSize:9, padding:'1px 6px', borderRadius:8, textTransform:'uppercase', letterSpacing:'.04em' }}>{s.replace('_',' ')}</span>;
            };
            return (
              <div style={{ padding: 10, background: '#1e293b', color: '#fff', borderRadius: 6, fontSize: 11 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 6 }}>
                  <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#a5b4fc' }}>Funding programs - {fd.state ?? '-'}</span>
                  <span style={{ fontSize: 10, color: '#86efac' }}>{fd.selected_count} selected, {fd.open_count} open</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:3, fontSize:10, color:'#cbd5e1', marginBottom:8 }}>
                  <span>BEAD passings</span><span style={{textAlign:'right'}}>{fmtNum(fd.passings.bead)}</span>
                  <span>RDOF passings</span><span style={{textAlign:'right'}}>{fmtNum(fd.passings.rdof)}</span>
                  <span>Private (unsubsidized)</span><span style={{textAlign:'right'}}>{fmtNum(fd.passings.private)}</span>
                  <span style={{fontWeight:700,borderTop:'1px solid #334155',paddingTop:2}}>Total passings</span>
                  <span style={{textAlign:'right',fontWeight:700,borderTop:'1px solid #334155',paddingTop:2}}>{fmtNum(fd.passings.total)}</span>
                </div>
                {fd.passings.rdof > 0 && (
                  <div style={{ padding:'6px 8px', background:'#7c2d12', color:'#fed7aa', borderRadius:4, fontSize:10, marginBottom:8, lineHeight:1.4 }}>
                    ⚠ Your company already holds awards on <b>{fmtNum(fd.passings.rdof)}</b> locations in this polygon (RDOF). Committed - cannot be re-funded by another grant (reason-code-4). A defaulted award frees them back up; manage status in Settings.
                  </div>
                )}
                {(() => { const avail = fd.programs.filter((p) => p.status !== 'closed' && p.status !== 'expired').length; return (
                  <button onClick={() => setGrantModal(true)} style={{ width:'100%', padding:'6px', marginBottom:6, fontSize:11, fontWeight:700, background:'#334155', color:'#a5b4fc', border:'1px dashed #475569', borderRadius:4, cursor:'pointer' }}>⊕ Select / manage grants ({avail} available)</button>
                ); })()}
                {fd.programs.filter((p) => p.selected).map((p) => (
                  <div key={p.program_key} style={{ borderTop:'1px solid #334155', paddingTop:5, marginTop:5 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:6 }}>
                      <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', flex:1 }}><input type="checkbox" checked={!!p.selected} onChange={() => toggleGrant(p.program_key, !!p.selected)} /><span style={{ fontWeight:600 }}>{p.params_reviewed ? <span style={{ color:'#34d399' }} title="Figures verified from official source">✓ </span> : null}{p.name}</span></label>{badge(p.status)}
                    </div>
                    <div style={{ fontSize:9, color:'#94a3b8', marginTop:2 }}>
                      {p.tier} · {p.agency} · {p.min_speed}{p.match_pct!=null?(' · '+Math.round(p.match_pct*100)+'% match'):''}{p.is_enforceable_commitment?' · enforceable commitment':''}
                    </div>
                    <div style={{ fontSize:9, color:'#7dd3fc', marginTop:2 }}>
                      {(p as any).est_eligible_passings != null ? (fmtNum((p as any).est_eligible_passings)+' eligible passings') : ''}{(p as any).eligibility_note ? (((p as any).est_eligible_passings != null ? ' · ' : '')+(p as any).eligibility_note) : ''}
                    </div>
                    {p.stacking_notes && <div style={{ fontSize:9, color:'#cbd5e1', marginTop:3, lineHeight:1.4 }}>{p.stacking_notes}</div>}
                    {p.sources && p.sources.length>0 && (
                      <div style={{ fontSize:9, marginTop:3 }}>
                        {p.sources.slice(0,2).map((s,i)=>(<a key={i} href={s.url} target="_blank" rel="noreferrer" style={{ color:'#7dd3fc', marginRight:8 }}>{s.title}</a>))}
                      </div>
                    )}
                  </div>
                ))}
                {(() => {
                  const sel = fd.programs.filter((p) => p.selected);
                  if (sel.length === 0) {
                    return <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #334155', fontSize:10, color:'#fbbf24' }}>Nothing selected - estimate is all private capital: <b>{fmtUSD(totalBuildCapex)}</b></div>;
                  }
                  const beadSel = sel.some((p) => /bead/i.test(p.program_key));
                  const opSupport = sel.filter((p) => p.funding_model === 'operating_support');
                  const loans = sel.filter((p) => p.funding_model === 'loan_grant');
                  const otherCapex = sel.filter((p) => p.funding_model === 'capex_grant' && !/bead/i.test(p.program_key));
                  const withParams = otherCapex.filter((p: any) => p.max_award_usd || p.federal_share_pct);
                  const stillPending = otherCapex.filter((p: any) => !p.max_award_usd && !p.federal_share_pct);
                  const capexOffset = beadSel ? grantFederal : 0;
                  const privateNeeded = Math.max(0, totalBuildCapex - capexOffset);
                  return (
                    <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid #334155', fontSize:10, color:'#cbd5e1', lineHeight:1.5 }}>
                      <div style={{ fontWeight:700, color:'#a5b4fc', marginBottom:4, letterSpacing:'.04em' }}>SELECTED FUNDING STACK</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:2 }}>
                        <span>Engineered capex</span><span style={{textAlign:'right'}}>{fmtUSD(totalBuildCapex)}</span>
                        {beadSel && (<><span>- BEAD capex grant (federal)</span><span style={{textAlign:'right',color:'#6ee7b7'}}>{fmtUSD(grantFederal)}</span></>)}
                        <span style={{fontWeight:700,borderTop:'1px solid #334155',paddingTop:2}}>Private capital needed</span>
                        <span style={{textAlign:'right',fontWeight:700,borderTop:'1px solid #334155',paddingTop:2}}>{fmtUSD(privateNeeded)}</span>
                      </div>
                      {opSupport.length>0 && <div style={{marginTop:4,color:'#67e8f9'}}>+ 10-yr operating support: {opSupport.map((p) => p.name).join(', ')} (see RDOF panel for amount)</div>}
                      {loans.length>0 && <div style={{marginTop:4}}>+ loan financing available: {loans.map((p) => p.name).join(', ')}</div>}
                      {withParams.length>0 && (
                        <div style={{marginTop:6, paddingTop:6, borderTop:'1px dashed #334155'}}>
                          <div style={{color:'#a5b4fc', fontWeight:600, marginBottom:2}}>Potential stackable grants (if awarded)</div>
                          {withParams.map((p: any) => (
                            <div key={p.program_key} style={{display:'flex', justifyContent:'space-between', gap:6}}>
                              <span>{p.name}</span>
                              <span style={{color:'#6ee7b7', whiteSpace:'nowrap'}}>{p.max_award_usd?('up to '+fmtUSD(Number(p.max_award_usd))):''}{p.federal_share_pct?(' · '+Math.round(Number(p.federal_share_pct)*100)+'% fed'):''}</span>
                            </div>
                          ))}
                          <div style={{fontSize:9, color:'#94a3b8', marginTop:2}}>Competitive, capped grants - reduce private capital further if awarded. Not guaranteed; not folded into the figure above.</div>
                        </div>
                      )}
                      {stillPending.length>0 && <div style={{marginTop:4,color:'#fbbf24'}}>{stillPending.length} other capex grant(s) selected - $ amount pending catalog data</div>}
                    </div>
                  );
                })()}
                <div style={{ marginTop:10, paddingTop:8, borderTop:'1px solid #334155' }}>
                  <button onClick={createComplianceProject} disabled={bridgeBusy} style={{ width:'100%', padding:'6px 10px', fontSize:11, fontWeight:700, background:'#4338ca', color:'#fff', border:'none', borderRadius:4, cursor:'pointer' }}>
                    {bridgeBusy ? 'Creating...' : 'Create compliance project from selected grants →'}
                  </button>
                  {bridgeMsg && <div style={{ marginTop:6, fontSize:10, color:'#a5f3fc', lineHeight:1.4 }}>{bridgeMsg}</div>}
                  <div style={{ fontSize:9, color:'#64748b', marginTop:4 }}>Promotes this plan into a compliance project; the permit page then auto-generates the obligations for these grants.</div>
                </div>
                <div style={{ fontSize:9, color:'#94a3b8', marginTop:8, lineHeight:1.4 }}>
                  Per-house funding: each passing draws from one program; different-program passings share one trunkline. Catalog maintained centrally in GeoHub.
                </div>
              </div>
            );
          })()}
          {/* BEAD compliance: uncovered awarded BSLs in territory */}
          {(plan.summary?.bead_uncovered ?? 0) > 0 && (
            <div style={{ padding: 10, background: '#7f1d1d', color: '#fff', borderRadius: 6, fontSize: 11 }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: '#fca5a5', marginBottom: 4 }}>
                BEAD compliance risk
              </div>
              <div>
                <b>{plan.summary.bead_uncovered}</b> BEAD-awarded BSLs in this territory are OUTSIDE any detected cluster
                ({plan.summary.bead_covered_by_clusters}/{plan.summary.bead_in_territory} covered).
                These must still be served under grant terms — consider lowering cluster minpoints or adding isolated-BSL handling.
              </div>
            </div>
          )}
          {onRegenerateBackbone && (
            <div style={{ padding: '10px 12px', background: (plan.backbone?.edge_count ?? 0) > 0 ? 'var(--bg-subtle, #1e293b)' : '#7c2d12', borderRadius: 6, marginBottom: 4 }}>
              <div style={{ fontSize: 11, color: '#fff', marginBottom: 6 }}>
                {hasHeadend
                  ? ((plan.backbone?.edge_count ?? 0) > 0
                      ? `Backhaul: ${plan.backbone.edge_count} branches, ${plan.summary?.backbone_length_mi ?? 0} mi`
                      : 'Head-end placed — no backhaul computed yet')
                  : 'Place a head-end on the map, then regenerate the backhaul.'}
              </div>
              {(() => {
                const p = plan?.progress ?? {};
                const inFlight = p.phase === 'computing_backbone';
                const total = Number(p.total ?? 0);
                const done = Number(p.done ?? 0);
                const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (regeneratingBackbone ? 5 : 0);
                const busy = inFlight || regeneratingBackbone;
                return (
                  <button
                    onClick={() => {
                      if (!onRegenerateBackbone) return;
                      const effective: number[] = [];
                      for (let i = 0; i < perCluster.length; i++) {
                        if (mainOn(i) || (beadOn(i) && Number(clusters[i]?.bead_awarded_count ?? 0) > 0)) effective.push(i);
                      }
                      onRegenerateBackbone(effective);
                    }}
                    disabled={!hasHeadend || busy}
                    title={busy ? `${done}/${total} ${p.current ?? ''}` : 'Compute backhaul routes from head-end to every cluster POP.'}
                    style={{ position: 'relative', overflow: 'hidden', width: '100%', padding: '10px 12px',
                             borderRadius: 6, border: 'none',
                             background: hasHeadend ? '#475569' : '#334155',
                             color: '#fff', fontSize: 12, fontWeight: 600,
                             cursor: hasHeadend && !busy ? 'pointer' : 'not-allowed' }}
                  >
                    {busy && (
                      <div
                        style={{ position: 'absolute', left: 0, top: 0, bottom: 0,
                                 width: `${pct}%`, background: '#f59e0b', opacity: 0.9,
                                 transition: 'width 400ms ease' }}
                      />
                    )}
                    {!busy && hasHeadend && (
                      <div style={{ position: 'absolute', inset: 0, background: '#f59e0b' }} />
                    )}
                    <span style={{ position: 'relative', zIndex: 1, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                      {busy
                        ? `Generating backhaul… ${done}/${total}${p.current ? ` · ${p.current}` : ''}`
                        : '◆ Generate backhaul from head-end'}
                    </span>
                  </button>
                );
              })()}
            </div>
          )}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)' }} title="Click a zone name to drill in and edit its boundary / extend FTTH">Zones — include in plan (click name to edit)</span>
              <span style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setBuildIndices(s.per_cluster.map((_: any, i: number) => i))} style={{ fontSize: 9, padding: '1px 6px', cursor: 'pointer', background: 'none', border: '1px solid var(--border)', borderRadius: 3 }}>All</button>
                <button onClick={() => setBuildIndices([])} style={{ fontSize: 9, padding: '1px 6px', cursor: 'pointer', background: 'none', border: '1px solid var(--border)', borderRadius: 3 }}>None</button>
              </span>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead><tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                  <th style={{ padding: '3px 2px', width: 22 }}>✓</th><th style={{ padding: '3px' }}>Zone</th><th style={{ padding: '3px' }}>Homes</th><th style={{ padding: '3px' }}>Capex</th><th style={{ padding: '3px' }}>Pay</th>
                </tr></thead>
                <tbody>{s.per_cluster.flatMap((c: any, i: number) => {
                  const checked = buildSet.has(i);
                  const cluster = (plan.clusters ?? [])[i];
                  const beadCount = Number(cluster?.bead_awarded_count ?? 0);
                  const privateCount = Math.max(0, Number(c.passings ?? 0) - beadCount);
                  const totalCapex = Number(c.capex ?? 0);
                  const privateCapex = totalCapex * (c.passings > 0 ? privateCount / c.passings : 1);
                  const beadCapex   = totalCapex * (c.passings > 0 ? beadCount / c.passings : 0);
                  // Scale c.mrr (at auto-design's 40% baseline) to current
                  // user take rates per segment. Private/main row uses the
                  // already-served baseline; BEAD row uses the unserved one
                  // (though BEAD row displays "covered by grant" not a
                  // dollar payback, so its scaling is informational).
                  const TR_BASE = 0.40;
                  const privateMrr  = Number(c.mrr ?? 0)
                    * (c.passings > 0 ? privateCount / c.passings : 0)
                    * ((takeRateServedPct / 100) / TR_BASE);
                  const beadMrr     = Number(c.mrr ?? 0)
                    * (c.passings > 0 ? beadCount / c.passings : 0)
                    * ((takeRateUnservedPct / 100) / TR_BASE);
                  const rows: any[] = [];
                  rows.push(
                    <tr key={`${i}-main`} style={{ borderBottom: '1px solid var(--border)', opacity: checked ? 1 : 0.5 }}>
                      <td style={{ padding: '3px 2px' }}><input type="checkbox" checked={checked} onChange={() => {
                        // Main row = PRIVATE subset only. Unchecking skips the private
                        // passings from the plan. If the BEAD subset is ALSO unchecked
                        // (meaning no BEAD coverage AND no private build), warn — that's
                        // effectively deleting the cluster with BEAD BSLs.
                        if (checked && beadCount > 0 && beadExcluded.has(i)) {
                          const msg = `${c.name} has ${beadCount} BEAD-awarded BSLs and its BEAD subset is already excluded. Unchecking this too means the cluster is entirely skipped — BEAD compliance violation.\n\nAre you SURE?`;
                          if (!window.confirm(msg)) return;
                        }
                        setBuildIndices((prev) => checked ? prev.filter((x) => x !== i) : [...prev, i]);
                      }} /></td>
                      <td style={{ padding: '3px', cursor: cluster?.zone_id ? 'pointer' : 'default' }} onClick={() => cluster?.zone_id && onZoneClick(cluster.zone_id)}>
                        <span style={{ color: '#6366f1', textDecoration: 'underline' }}>{c.name}</span>
                        {!checked && <span style={{ fontSize: 8, color: '#dc2626', marginLeft: 3 }}>skip</span>}
                      </td>
                      <td style={{ padding: '3px' }}>{fmtNum(privateCount > 0 ? privateCount : c.passings)}</td>
                      <td style={{ padding: '3px' }}>{checked ? fmtUSD(privateCount > 0 ? privateCapex : totalCapex) : '—'}</td>
                      <td style={{ padding: '3px', color: ((privateMrr > 0 ? privateCapex / (privateMrr * grossMargin) : 999) < 36 ? '#10b981' : '#f59e0b') }}>{checked ? fmtMo(privateMrr > 0 ? privateCapex / (privateMrr * grossMargin) : null) : '—'}</td>
                    </tr>
                  );
                  if (beadCount > 0) {
                    const beadChecked = beadSubsetChecked(i);
                    rows.push(
                      <tr key={`${i}-bead`} style={{ borderBottom: '1px solid var(--border)', background: beadChecked ? '#dcfce7' : '#fef3c7', opacity: beadChecked ? 1 : 0.55 }}>
                        <td style={{ padding: '3px 2px' }}>
                          <input
                            type="checkbox"
                            checked={beadChecked}
                            onChange={() => {
                              if (beadChecked) {
                                const msg = `${c.name} includes ${beadCount} BEAD-awarded BSLs. Excluding them from the plan means those passings will NOT be served — BEAD compliance risk ($20M+ penalty, grant reclaim).\n\nAre you SURE you want to exclude the BEAD subset?`;
                                if (!window.confirm(msg)) return;
                                toast.warning(`BEAD compliance risk: ${beadCount} awarded BSLs excluded`);
                              }
                              setBeadExcluded((prev) => {
                                const next = new Set(prev);
                                if (beadChecked) next.add(i); else next.delete(i);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td style={{ padding: '3px', paddingLeft: 16, fontSize: 11 }}>
                          <span style={{ color: beadChecked ? '#065f46' : '#92400e' }}>↳ BEAD subset of {c.name}</span>
                          <span style={{ display: 'inline-block', marginLeft: 4, padding: '0 4px', borderRadius: 3, fontSize: 8, fontWeight: 700, background: beadChecked ? '#059669' : '#dc2626', color: '#fff', verticalAlign: 'middle' }}>GRANT</span>
                          {!beadChecked && <span style={{ marginLeft: 4, fontSize: 8, color: '#dc2626', fontWeight: 700 }}>⚠ SKIPPED</span>}
                        </td>
                        <td style={{ padding: '3px', color: beadChecked ? '#065f46' : '#92400e', fontWeight: 600 }}>{fmtNum(beadCount)}</td>
                        <td style={{ padding: '3px', color: beadChecked ? '#065f46' : '#92400e' }}>{beadChecked ? fmtUSD(beadCapex) : '—'}</td>
                        <td style={{ padding: '3px', fontSize: 10, color: beadChecked ? '#065f46' : '#92400e' }}>{beadChecked ? 'covered by grant' : 'excluded — COMPLIANCE RISK'}</td>
                      </tr>
                    );
                  }
                  return rows;
                })}</tbody>
              </table>
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-subtle)', marginTop: 4 }}>Click a zone name to open it. Unchecked = backbone pass-through (no FTTH).</div>
          </div>

          {(plan.backbone?.edges ?? []).length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)', marginBottom: 4 }}>
                Backbone ({plan.backbone.edge_count} edges, {s.backbone_length_mi} mi)
              </div>
              <div style={{ display: 'flex', gap: 6, fontSize: 11, marginBottom: 10, flexWrap: 'wrap' }}>
                {(['fiber_build', 'wireless_ptp', 'ride_along'] as const).map((m) => {
                  const on = showModes[m];
                  const color = MODE_COLOR[m];
                  return (
                    <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 12, cursor: 'pointer', border: `1.5px solid ${on ? color : 'var(--border)'}`, background: on ? color + '22' : 'transparent', opacity: on ? 1 : 0.5, userSelect: 'none' }}>
                      <input type="checkbox" checked={on} onChange={(e) => setShowModes((prev) => ({ ...prev, [m]: e.target.checked }))} style={{ margin: 0, accentColor: color }} />
                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color, border: '1px solid #0009' }} />
                      <span style={{ fontWeight: 600 }}>{MODE_LABEL[m]}</span>
                    </label>
                  );
                })}
              </div>
              {plan.backbone.edges.map((e: any, i: number) => {
                const h = e.hybrid_result;
                const totalMi = e.road_distance_mi ?? e.straight_distance_mi ?? h?.total_distance_mi ?? 0;
                return (
                  <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5, fontSize: 11 }}>
                      <span style={{ fontWeight: 600 }}>{e.from_name} → {e.to_name}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{Number(totalMi).toFixed(1)} mi · <b style={{ color: 'var(--text)' }}>{h ? fmtUSD(h.total_capex) : '—'}</b></span>
                    </div>
                    {h && (
                      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        {h.segments.map((seg: any, j: number) => (
                          <div key={j} title={`${MODE_LABEL[seg.mode]} · ${seg.distance_mi.toFixed(2)} mi · ${fmtUSD(seg.capex)}`} style={{ width: `${(seg.distance_mi / (h.total_distance_mi || 1)) * 100}%`, background: MODE_COLOR[seg.mode] || '#94a3b8' }} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px' }}>
      <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color ?? 'var(--text)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

// --------------------------------------------------------------------
// Soil Profile card — pulls /geohub-data/soil-profile for the territory.
// Renders a one-line description, construction-method breakdown bars,
// blended labor rate vs catalog default, and a wetland warning.
// --------------------------------------------------------------------

type SoilProfile = {
  input: { kind: 'polygon' | 'linestring'; area_m2: number | null;
           length_m: number | null; intersected_area_m2: number };
  soil_profile: {
    dominant_drainage: string | null;
    dominant_hydro_group: string | null;
    avg_slope_pct: number | null;
    avg_bedrock_depth_cm: number | null;
    hydric_pct_weighted: number | null;
    map_unit_count: number;
    top_soils: Array<{ musym: string; muname: string; area_pct: number }>;
  } | null;
  construction_method_breakdown: Record<'plow'|'trench'|'rock_saw'|'directional_bore',
    { pct: number; labor_per_ft: number }> | null;
  blended_labor_per_ft: number;
  catalog_default_per_ft: number;
  adjustment_per_ft: number;
  // 2026-06-12: per-component breakdown of the blended rate (buried-soil weighted)
  blended_components?: {
    base: number | null;
    traffic: number | null;
    hardware: number | null;
    restoration: number | null;
  };
  aerial_components?: {
    base: number | null;
    traffic: number;
    hardware: number;
    restoration: number;
  };
  weighted_by?: 'area' | 'route' | 'area_fallback';
  route_length_m?: number | null;
  wetland_risk: {
    hydric_majority_area_m2: number;
    hydric_majority_area_pct: number;
    hydric_polygon_count: number;
    needs_404_review: boolean;
  } | null;
  note?: string;
};

function SoilProfileCard({ planId, aerialMixPct, aerialRate }: { planId: string; aerialMixPct: number; aerialRate: number }) {
  // Try route-weighted first (more accurate); on error/timeout, fall back
  // to area-weighted so the card always renders for plans with SSURGO
  // coverage. Big territories (~50+ mi route) will silently use area mode.
  const { data, isLoading, error } = useQuery<SoilProfile>({
    queryKey: ['soil-profile', planId],
    queryFn: async () => {
      try {
        return await api.get(`/geohub-data/soil-profile?territory_plan_id=${planId}&weighted_by=route`);
      } catch (e) {
        // Route-weighted failed — retry with area-weighted (the polygon
        // intersect is cheaper and the result still drives the card).
        return await api.get(`/geohub-data/soil-profile?territory_plan_id=${planId}&weighted_by=area`);
      }
    },
    enabled: !!planId,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  if (isLoading || error || !data?.soil_profile || !data?.construction_method_breakdown) {
    return null;
  }

  const sp = data.soil_profile;
  const mb = data.construction_method_breakdown;
  const wr = data.wetland_risk;

  const drainage = (sp.dominant_drainage ?? '').replace(/\s+/g, ' ').trim();
  const slope    = sp.avg_slope_pct ?? 0;
  const bedrock  = sp.avg_bedrock_depth_cm;
  const hydric   = sp.hydric_pct_weighted ?? 0;
  const topName  = sp.top_soils?.[0]?.muname ?? '(unnamed soil)';

  // One-line description
  const slopeBand = slope < 5 ? 'low slope' : slope < 15 ? 'medium slope' : 'steep slope';
  const bedrockBand = bedrock == null ? 'bedrock unknown'
                    : bedrock < 46 ? 'shallow bedrock'
                    : bedrock < 91 ? 'moderate bedrock'
                    : 'deep bedrock';
  const wetlandBand = hydric < 5 ? '' : `· ${hydric.toFixed(0)}% wetland risk`;
  const desc = `Mostly ${topName.split(',')[0]}. ${drainage}. ${slopeBand}, ${bedrockBand} ${wetlandBand}`.trim();

  const methodLabels: Record<string, string> = {
    plow: 'Plow', trench: 'Trench', rock_saw: 'Rock saw', directional_bore: 'Bore',
  };
  const methodColors: Record<string, string> = {
    plow: '#10b981', trench: '#3b82f6', rock_saw: '#f59e0b', directional_bore: '#ef4444',
  };
  const methodOrder = ['plow', 'trench', 'rock_saw', 'directional_bore'] as const;

  const fmt2 = (n: number) => `$${n.toFixed(2)}/ft`;

  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--surface-2, #f8fafc)',
      border: '1px solid var(--border)',
      borderRadius: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)' }}>
          Soil profile
        </span>
        <span style={{ fontSize: 9, color: 'var(--text-subtle, #94a3b8)' }}>
          {sp.map_unit_count} map units · SSURGO{data.weighted_by === 'route' && data.route_length_m ? ` · route-weighted (${(data.route_length_m / 1609.34).toFixed(1)} mi)` : data.weighted_by === 'area_fallback' ? ' · area-weighted (no fiber yet)' : ' · area-weighted'}
        </span>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text)', marginBottom: 8, lineHeight: 1.4 }}>
        {desc}
      </div>

      {/* Construction method breakdown bars */}
      <div style={{ marginBottom: 8 }}>
        {methodOrder.map((m) => {
          const row = mb[m];
          const pct = Math.max(0, Math.min(100, row.pct));
          return (
            <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, marginBottom: 3 }}>
              <span style={{ width: 56, color: 'var(--text-muted)' }}>{methodLabels[m]}</span>
              <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`, height: '100%',
                  background: methodColors[m], transition: 'width 0.3s',
                }} />
              </div>
              <span style={{ width: 36, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {pct.toFixed(0)}%
              </span>
              <span style={{ width: 56, textAlign: 'right', color: 'var(--text-subtle, #94a3b8)', fontVariantNumeric: 'tabular-nums' }}>
                {fmt2(row.labor_per_ft)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Aerial-aware blended summary */}
      {(() => {
        const aerialFrac = Math.max(0, Math.min(1, aerialMixPct / 100));
        const buriedFrac = 1 - aerialFrac;
        const buriedSoil = data.blended_labor_per_ft;
        const total = aerialFrac * aerialRate + buriedFrac * buriedSoil;
        const catalogTotal = aerialFrac * aerialRate + buriedFrac * data.catalog_default_per_ft;
        const adj = total - catalogTotal;
        return (
          <div style={{
            padding: '8px 10px', background: '#fff', borderRadius: 4,
            border: '1px solid var(--border)', marginBottom: 6,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 4, fontSize: 10, color: 'var(--text-muted)', marginBottom: 4 }}>
              <span>Aerial portion ({Math.round(aerialFrac * 100)}% × {fmt2(aerialRate)})</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt2(aerialFrac * aerialRate)}</span>
              <span>Buried-soil portion ({Math.round(buriedFrac * 100)}% × {fmt2(buriedSoil)})</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt2(buriedFrac * buriedSoil)}</span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              borderTop: '1px solid var(--border)', paddingTop: 4,
            }}>
              <div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                  Total blended labor
                </div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt2(total)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                  vs catalog blend
                </div>
                <div style={{
                  fontSize: 12, fontWeight: 700,
                  color: adj > 0.5 ? '#ef4444' : adj < -0.5 ? '#10b981' : '#64748b',
                }}>
                  {adj >= 0 ? '+' : ''}{fmt2(adj)}
                </div>
              </div>
            </div>
            {/* 2026-06-12: per-component breakdown of the blended rate so the
                user can see where each $/ft is coming from (base catalog rate
                vs each adder bucket). Blended across aerial + buried. */}
            {(() => {
              const bc = data.blended_components;
              const ac = data.aerial_components;
              if (!bc || bc.base == null) return null;
              const blendComp = (key: 'base' | 'traffic' | 'hardware' | 'restoration') => {
                const a = ac && typeof ac[key] === 'number' ? Number(ac[key]) : 0;
                const b = typeof bc[key] === 'number' ? Number(bc[key]) : 0;
                return aerialFrac * a + buriedFrac * b;
              };
              const cb = blendComp('base');
              const ct = blendComp('traffic');
              const ch = blendComp('hardware');
              const cr = blendComp('restoration');
              const fmtComp = (n: number) => `$${n.toFixed(2)}`;
              return (
                <div style={{
                  marginTop: 6, paddingTop: 6,
                  borderTop: '1px dashed var(--border)',
                  fontSize: 9, color: 'var(--text-muted)',
                  lineHeight: 1.4,
                }}>
                  Blended from: <b>{fmtComp(cb)}</b> base + <b>{fmtComp(ct)}</b> traffic + <b>{fmtComp(ch)}</b> hardware + <b>{fmtComp(cr)}</b> restoration
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Wetland warning */}
      {wr?.needs_404_review && (
        <div style={{
          padding: '6px 8px', background: '#fef3c7', border: '1px solid #fde68a',
          borderRadius: 4, fontSize: 10, color: '#92400e',
        }}>
          ⚠ {wr.hydric_majority_area_pct.toFixed(1)}% of territory is hydric (likely wetland)
          · {wr.hydric_polygon_count} polygons · §404 review may be required
        </div>
      )}
    </div>
  );
}
